import type { Key } from "react";

import type { Vero1CardVerdict } from "manager/components/vero1/vero1BannerState";
import {
  effectiveTone,
  RowTone,
} from "manager/components/vero1/Vero1ReviewPanel/formatItemValue";
import { isLeasingTouched } from "manager/components/vero1/Vero1ReviewPanel/itemState";
import type { Vero1ChecklistItem } from "shared/interfaces/api/vero1";

import { CategoryId } from "../../constants";

const CATEGORY_TO_CARD: Record<string, CategoryId> = {
  identity: CategoryId.ID_VERIFICATION,
  income: CategoryId.INCOME,
  credit: CategoryId.CREDIT_HISTORY,
  rental: CategoryId.RENTAL_HISTORY,
  criminal: CategoryId.CRIMINAL_HISTORY,
};

const SEVERITY: Record<RowTone, number> = { pass: 0, warn: 1, fail: 2 };

const BACKGROUND_CARDS = [
  CategoryId.CREDIT_HISTORY,
  CategoryId.RENTAL_HISTORY,
  CategoryId.CRIMINAL_HISTORY,
];

const GATE_KEYS = new Set([
  "background-reports-run",
  "all-background-reports-run",
]);

export interface Vero1CardOutcome {
  tone: RowTone;
  editedCount: number;
  reviewCategory?: string;
}

export type Vero1CardOutcomes = Partial<Record<CategoryId, Vero1CardOutcome>>;

export const vero1CardsGovern = (
  verdict: Vero1CardVerdict | null | undefined
): boolean => verdict?.mode === "adopt" || verdict?.mode === "override";

type CardItem = Pick<
  Vero1ChecklistItem,
  | "category"
  | "originalSeverity"
  | "overrideSeverity"
  | "overrideExplanation"
  | "reasonCode"
  | "actionForLeasing"
> &
  Partial<
    Pick<
      Vero1ChecklistItem,
      "criterionKey" | "application" | "outcomeOptions" | "overrideOutcomeName"
    >
  >;

const collectionsCard = (item: CardItem): CategoryId =>
  (item.overrideOutcomeName ?? "").toLowerCase().includes("rental") ||
  (item.outcomeOptions?.fired ?? []).some((name) =>
    name.toLowerCase().includes("rental")
  )
    ? CategoryId.RENTAL_HISTORY
    : CategoryId.CREDIT_HISTORY;

const cardsFor = (item: CardItem): CategoryId[] => {
  if (item.criterionKey && GATE_KEYS.has(item.criterionKey))
    return BACKGROUND_CARDS;
  const category = (item.category ?? "").toLowerCase();
  if (category === "collections") return [collectionsCard(item)];
  const card = CATEGORY_TO_CARD[category];
  return card ? [card] : [];
};

const inScope = (
  item: CardItem,
  applicationId: Key | null | undefined,
  applicantScope: boolean
) =>
  !applicantScope ||
  item.application == null ||
  (applicationId != null && String(item.application) === String(applicationId));

export const getVero1CardOutcomes = (
  items: CardItem[],
  {
    backgroundIncomplete = false,
    applicationId,
    applicantScope = applicationId != null,
  }: {
    backgroundIncomplete?: boolean;
    applicationId?: Key | null;
    applicantScope?: boolean;
  } = {}
): Vero1CardOutcomes => {
  const outcomes: Vero1CardOutcomes = {};
  const edits: Partial<Record<CategoryId, { rank: number; item: CardItem }[]>> =
    {};

  items.forEach((item) => {
    if (!inScope(item, applicationId, applicantScope)) return;
    const tone = effectiveTone(item);
    if (!tone) return;
    cardsFor(item).forEach((card) => {
      const current = outcomes[card];
      if (!current || SEVERITY[tone] > SEVERITY[current.tone]) {
        outcomes[card] = { tone, editedCount: current?.editedCount ?? 0 };
      }
      if (!isLeasingTouched(item)) return;
      (outcomes[card] as Vero1CardOutcome).editedCount += 1;
      if (!edits[card]) edits[card] = [];
      (edits[card] as { rank: number; item: CardItem }[]).push({
        rank: SEVERITY[tone],
        item,
      });
    });
  });

  if (backgroundIncomplete) {
    BACKGROUND_CARDS.forEach((card) => {
      if (outcomes[card]?.tone === "pass" && !edits[card]?.length) {
        delete outcomes[card];
      }
    });
  }

  (Object.keys(edits) as CategoryId[]).forEach((card) => {
    const outcome = outcomes[card];
    const rows = edits[card];
    if (!outcome || !rows) return;
    outcome.reviewCategory =
      rows
        .slice()
        .sort((a, b) => b.rank - a.rank)
        .map(({ item }) => (item.category ?? "").toLowerCase())
        .find(Boolean) ?? undefined;
  });

  return outcomes;
};
